import React from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCalendarAlt,
  FaCamera,
  FaChartBar,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import { GiTennisBall } from "react-icons/gi";
import "./DmrHomepage.css";

function DmrHomepage({ pageData }) {
  const navigate = useNavigate();

  const organization = pageData?.organization || {};
  const page = pageData?.page || {};
  const content = page?.content || {};

  const organizationName = organization.name || "";

  const seasonLabel = content.season_label || "";
  const heroTitle = content.hero_title || organizationName;
  const heroAccentTitle = content.hero_accent_title || "";
  const heroSubtitle = content.hero_subtitle || "";

  const heroImage = content.hero_image_url || "";

  const primaryButtonLabel = content.primary_button_label || "";
  const primaryButtonLink = content.primary_button_link || "";

  const secondaryButtonLabel = content.secondary_button_label || "";
  const secondaryButtonLink = content.secondary_button_link || "";

  const featuresEyebrow = content.features_eyebrow || "";
  const featuresTitle = content.features_title || "";

  const iconMap = {
    calendar: FaCalendarAlt,
    camera: FaCamera,
    chart: FaChartBar,
    player: FaUser,
    players: FaUsers,
    tennis: GiTennisBall,
  };

  const defaultFeatureCards = [
    {
      icon: "chart",
      title: "MATCH STATS",
      description:
        "Detailed stats for every match. Track your performance all season long.",
    },
    {
      icon: "camera",
      title: "MEDIA HIGHLIGHTS",
      badge: "COMING SOON",
      description:
        "Photos and videos from league matches and featured moments.",
    },
    {
      icon: "player",
      title: "PLAYER PROFILES",
      description:
        "Individual player pages with stats, records, and season performance.",
    },
    {
      icon: "calendar",
      title: "SCHEDULE & RESULTS",
      description:
        "Up-to-date schedules, scores, and standings all in one place.",
    },
  ];
  const featureCards =
    Array.isArray(content.feature_cards) && content.feature_cards.length
      ? content.feature_cards
      : defaultFeatureCards;

  const openLink = (link) => {
    const rawLink = String(link || "").trim();

    if (!rawLink) {
      return;
    }

    const isExternal =
      rawLink.startsWith("http://") || rawLink.startsWith("https://");

    const isSpecialProtocol =
      rawLink.startsWith("mailto:") || rawLink.startsWith("tel:");

    const looksLikeDomain =
      !rawLink.startsWith("/") &&
      !rawLink.startsWith("./") &&
      !rawLink.startsWith("../") &&
      rawLink.includes(".");

    if (isExternal || isSpecialProtocol) {
      window.open(rawLink, "_blank", "noopener,noreferrer");
      return;
    }

    if (looksLikeDomain) {
      window.open(`https://${rawLink}`, "_blank", "noopener,noreferrer");
      return;
    }

    navigate(rawLink.startsWith("/") ? rawLink : `/${rawLink}`);
  };

  const renderIcon = (iconName, className) => {
    const IconComponent = iconMap[iconName] || GiTennisBall;

    return <IconComponent className={className} aria-hidden="true" />;
  };

  return (
    <main className="dmr-homepage">
      <section className="dmr-hero">
        <div className="dmr-hero-main">
          <div className="dmr-hero-content">
            {seasonLabel ? (
              <p className="dmr-hero-season-label">{seasonLabel}</p>
            ) : null}

            <div className="dmr-hero-title-container">
              {heroTitle ? (
                <h1 className="dmr-hero-title">{heroTitle}</h1>
              ) : null}

              {heroAccentTitle ? (
                <p className="dmr-hero-accent-title">{heroAccentTitle}</p>
              ) : null}
            </div>

            {heroSubtitle ? (
              <p className="dmr-hero-subtitle">{heroSubtitle}</p>
            ) : null}

            {primaryButtonLink || secondaryButtonLink ? (
              <div className="dmr-hero-actions">
                {primaryButtonLink ? (
                  <button
                    type="button"
                    className="dmr-btn dmr-btn-primary"
                    onClick={() => openLink(primaryButtonLink)}
                  >
                    <FaCalendarAlt aria-hidden="true" />
                    <span>{primaryButtonLabel}</span>
                  </button>
                ) : null}

                {secondaryButtonLink ? (
                  <button
                    type="button"
                    className="dmr-btn dmr-btn-secondary"
                    onClick={() => openLink(secondaryButtonLink)}
                  >
                    <FaUsers aria-hidden="true" />
                    <span>{secondaryButtonLabel}</span>
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>

          {heroImage ? (
            <div className="dmr-hero-image-container">
              <img
                src={heroImage}
                alt={heroTitle || organizationName || "DMR Tennis"}
                className="dmr-hero-image"
                fetchPriority="high"
                loading="eager"
              />
            </div>
          ) : null}
        </div>
      </section>

      <section className="dmr-includes-section">
        <div className="dmr-section-heading">
          {featuresEyebrow ? (
            <div className="dmr-section-eyebrow-row">
              <span className="dmr-section-eyebrow-line" aria-hidden="true" />
              <p className="dmr-section-eyebrow">{featuresEyebrow}</p>
              <span className="dmr-section-eyebrow-line" aria-hidden="true" />
            </div>
          ) : null}

          {featuresTitle ? (
            <h2 className="dmr-section-title">{featuresTitle}</h2>
          ) : null}
        </div>

        {featureCards.length ? (
          <div className="dmr-feature-card-grid">
            {featureCards.map((feature, index) => {
              const title = feature?.title || "";
              const description = feature?.description || "";
              const icon = feature?.icon || "tennis";

              if (!title && !description) {
                return null;
              }

              return (
                <article
                  className="dmr-feature-card"
                  key={`${title}-${index}`}
                >
                  <div className="dmr-feature-card-icon">
                    {renderIcon(icon, "dmr-feature-card-icon-svg")}
                  </div>

                  {title ? (
                    <h3 className="dmr-feature-card-title">{title}</h3>
                  ) : null}
                  {feature?.badge ? (
                    <span className="dmr-feature-card-badge">
                      {feature.badge}
                    </span>
                  ) : null}
                  <span
                    className="dmr-feature-card-divider"
                    aria-hidden="true"
                  />

                  {description ? (
                    <p className="dmr-feature-card-description">
                      {description}
                    </p>
                  ) : null}
                </article>
              );
            })}
          </div>
        ) : null}
      </section>
    </main>
  );
}

export default DmrHomepage;